// Sablonok (UI-3): every letter as a card with its picture.
//
// One request for the whole gallery (`GET /templates/overview`); each card's
// picture is the engine's own render of the newest version, asked for once
// the card is on screen (Thumbnail.tsx). Search, the state tabs and the order
// work on what is already here — a gallery is dozens of letters, not millions.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PlusIcon, SearchIcon } from "lucide-react";
import { archiveTemplate, fetchSendingIdentities, fetchTemplateOverview } from "@/lib/api";
import type { SendingIdentity, TemplateOverviewRow, TemplateStatus } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ActionDialog } from "@/components/emc/ActionDialog";
import { PageHeader } from "@/components/emc/PageHeader";
import { RowActions } from "@/components/emc/RowActions";
import type { RowAction } from "@/components/emc/RowActions";
import { StatusPill } from "@/components/emc/StatusPill";
import { Toolbar } from "@/components/emc/Toolbar";
import { EmptyState, ErrorState, LoadingState } from "@/components/emc/states";
import { count, minute } from "@/components/emc/format";
import { useAuth } from "@/lib/auth";
import { NewTemplateDialog } from "@/pages/templates/NewTemplateDialog";
import { Thumbnail } from "@/pages/templates/Thumbnail";
import {
  accountLanguages,
  cardStatus,
  copySourceOf,
  messageOf,
  renderCountryOf,
  variantLabel,
} from "@/pages/templates/model";
import type { CopySource } from "@/pages/templates/model";

const TABS: { key: string; label: string; status: TemplateStatus | null }[] = [
  { key: "osszes", label: "Összes", status: null },
  { key: "vazlat", label: "Vázlat", status: "draft" },
  { key: "publikalt", label: "Publikált", status: "published" },
  { key: "archivalt", label: "Archivált", status: "archived" },
];

const ORDERS = {
  szerkesztve: "Legutóbb szerkesztett elöl",
  nev: "Név szerint (A–Z)",
} as const;
type Order = keyof typeof ORDERS;

/** Lower case, accents off: "Árvíztűrő" is found by "arvizturo". */
function folded(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

export default function TemplateGallery() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [rows, setRows] = useState<TemplateOverviewRow[] | null>(null);
  const [identities, setIdentities] = useState<SendingIdentity[]>([]);
  // Until the accounts are known a thumbnail waits rather than reporting
  // that there are none.
  const [accountsKnown, setAccountsKnown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [copying, setCopying] = useState<CopySource | null>(null);
  const [archiving, setArchiving] = useState<TemplateOverviewRow | null>(null);

  const tab = TABS.find((entry) => entry.key === params.get("allapot")) ?? TABS[0]!;
  const order: Order = params.get("rend") === "nev" ? "nev" : "szerkesztve";
  const search = params.get("q") ?? "";
  // The copies of Klaviyo's flow templates (a few hundred) only when asked for:
  // the flow pages are where they are worked on.
  const klaviyoFlow = params.get("klaviyo") === "1";

  const setParam = (name: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(name);
    else next.set(name, value);
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    try {
      setRows(await fetchTemplateOverview(true, klaviyoFlow));
      setError(null);
    } catch (raised: unknown) {
      setError(messageOf(raised));
    }
  }, [klaviyoFlow]);

  useEffect(() => {
    void load();
    // The languages a letter can be written in are the ones an account speaks,
    // and the thumbnail is rendered with an account's footer — the one that
    // speaks the letter's language, or any active one (`renderCountryOf`).
    fetchSendingIdentities()
      .then(setIdentities)
      .catch(() => setIdentities([]))
      .finally(() => setAccountsKnown(true));
  }, [load]);

  const counts = useMemo(() => {
    const byStatus: Record<string, number> = { osszes: 0, vazlat: 0, publikalt: 0, archivalt: 0 };
    for (const row of rows ?? []) {
      const status = cardStatus(row);
      if (status !== "archived") byStatus.osszes = (byStatus.osszes ?? 0) + 1;
      const key = TABS.find((entry) => entry.status === status)?.key;
      if (key) byStatus[key] = (byStatus[key] ?? 0) + 1;
    }
    return byStatus;
  }, [rows]);

  const shown = useMemo(() => {
    const needle = folded(search.trim());
    const picked = (rows ?? []).filter((row) => {
      const status = cardStatus(row);
      if (tab.status === null ? status === "archived" : status !== tab.status) return false;
      return needle === "" || folded(`${row.name} ${row.key}`).includes(needle);
    });
    return picked.sort((a, b) =>
      order === "nev"
        ? a.name.localeCompare(b.name, "hu")
        : new Date(b.edited_at).getTime() - new Date(a.edited_at).getTime(),
    );
  }, [rows, tab, search, order]);

  const languages = useMemo(() => {
    const spoken = accountLanguages(identities);
    return spoken.length > 0 ? spoken : ["hu"];
  }, [identities]);

  function actionsFor(row: TemplateOverviewRow): RowAction[] {
    const actions: RowAction[] = [
      { label: "Megnyitás", onSelect: () => navigate(`/sablonok/${row.id}`) },
      // An archived letter too: last year's layout is still a layout.
      { label: "Másolat készítése", disabled: row.latest === null, onSelect: () => setCopying(copySourceOf(row, rows ?? [])) },
    ];
    if (isAdmin && !row.is_archived) {
      actions.push({ label: "Archiválás", danger: true, onSelect: () => setArchiving(row) });
    }
    return actions;
  }

  return (
    <>
      <PageHeader
        title="Sablonok"
        description="A levelek, amelyekből a kampányok készülnek. Egy sablon egyszer készül, és nyelvenként kap tárgyat, előnézeti szöveget és fordítást."
        actions={
          <Button type="button" onClick={() => setCreating(true)}>
            <PlusIcon />
            Új sablon
          </Button>
        }
      />

      <Tabs value={tab.key} onValueChange={(value) => setParam("allapot", value === "osszes" ? null : value)} className="mb-3">
        <TabsList aria-label="Állapot szerint">
          {TABS.map((entry) => (
            <TabsTrigger key={entry.key} value={entry.key}>
              {entry.label}
              {rows !== null && (
                <span className="text-label text-muted-foreground">{count(counts[entry.key] ?? 0)}</span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Toolbar className="mb-4">
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label="Keresés név vagy kulcs szerint"
            placeholder="Keresés név vagy kulcs szerint"
            className="w-72 pl-8"
            value={search}
            onChange={(event) => setParam("q", event.target.value)}
          />
        </div>
        <NativeSelect
          aria-label="Sorrend"
          value={order}
          onChange={(event) => setParam("rend", event.target.value === "nev" ? "nev" : null)}
        >
          {(Object.keys(ORDERS) as Order[]).map((key) => (
            <option key={key} value={key}>
              {ORDERS[key]}
            </option>
          ))}
        </NativeSelect>
        <label className="flex cursor-pointer items-center gap-2 text-label text-muted-foreground">
          <Switch
            checked={klaviyoFlow}
            onCheckedChange={(on) => setParam("klaviyo", on ? "1" : null)}
            aria-label="Klaviyo flow-sablonok"
          />
          Klaviyo flow-sablonok
        </label>
      </Toolbar>

      {error !== null && <ErrorState message={error} className="mb-4" onRetry={() => void load()} />}

      {rows === null && error === null ? (
        <LoadingState lines={4} />
      ) : rows !== null && rows.length === 0 ? (
        <EmptyState
          title="Még nincs sablon."
          description="Az „Új sablon” üres levelet nyit, vagy behoz egy régi Klaviyo-levelet HTML-ből."
          action={
            <Button type="button" onClick={() => setCreating(true)}>
              <PlusIcon />
              Új sablon
            </Button>
          }
        />
      ) : shown.length === 0 && rows !== null ? (
        <EmptyState
          title={search ? "Nincs a keresésnek megfelelő sablon." : `Nincs ${tab.label.toLowerCase()} sablon.`}
          description={search ? "Próbálj rövidebb szót, vagy nézd meg a többi fülön." : undefined}
        />
      ) : (
        <ul className="m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(--spacing(60),1fr))] gap-4 p-0">
          {shown.map((row) => (
            <li key={row.id}>
              <TemplateCard
                row={row}
                identities={accountsKnown ? identities : null}
                actions={actionsFor(row)}
              />
            </li>
          ))}
        </ul>
      )}

      <NewTemplateDialog
        open={creating}
        onOpenChange={setCreating}
        languages={languages}
        onCreated={() => void load()}
      />
      <NewTemplateDialog
        open={copying !== null}
        onOpenChange={(open) => !open && setCopying(null)}
        languages={languages}
        copyOf={copying}
      />

      <ActionDialog
        open={archiving !== null}
        onOpenChange={(open) => !open && setArchiving(null)}
        title={archiving ? `„${archiving.name}” archiválása` : "Archiválás"}
        description="Az archivált sablon kikerül a listából, és új kampányhoz nem választható. A verziói megmaradnak: ami már kiment vele, visszakereshető."
        confirmLabel="Archiválás"
        tone="danger"
        onConfirm={async () => {
          if (archiving === null) return;
          // A refusal — say, a live campaign still sends one of its versions —
          // is thrown back into the dialog, in the API's own words.
          await archiveTemplate(archiving.id);
          toast.success("A sablon archiválva.");
          await load();
        }}
      />
    </>
  );
}

function TemplateCard({
  row,
  identities,
  actions,
}: {
  row: TemplateOverviewRow;
  /** Null while the accounts are still being fetched. */
  identities: SendingIdentity[] | null;
  actions: RowAction[];
}) {
  const latest = row.latest;
  const href = `/sablonok/${row.id}`;
  const status = cardStatus(row);
  const olderPublished =
    row.published !== null && latest !== null && row.published.version !== latest.version ? row.published : null;

  return (
    <article className="flex h-full flex-col overflow-hidden rounded-card border border-line bg-surface shadow-emc">
      <Link to={href} tabIndex={-1} aria-hidden="true" className="block border-b border-line">
        {latest !== null ? (
          <Thumbnail
            versionId={latest.id}
            language={latest.default_language}
            country={
              identities === null
                ? undefined
                : renderCountryOf(identities, { language: latest.default_language, country: "" })
            }
          />
        ) : (
          <div className="aspect-[4/5] w-full bg-surface-2" />
        )}
      </Link>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <Link to={href} className="min-w-0 font-medium break-words text-ink hover:text-blue-ink">
            {row.name}
          </Link>
          <RowActions actions={actions} label={`${row.name}: műveletek`} />
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <StatusPill domain="template" state={status} />
          {latest !== null && <span className="data-mono text-label text-muted-foreground">v{latest.version}</span>}
          {olderPublished !== null && (
            <span className="text-label text-muted-foreground">
              kampányban: <span className="data-mono">v{olderPublished.version}</span>
            </span>
          )}
        </div>
        <p className="data-mono m-0 text-label text-muted-foreground" title="Nyelvek">
          {row.languages.length === 0 ? "—" : row.languages.map(variantLabel).join(" · ")}
        </p>
        <p className="mt-auto mb-0 text-label text-muted-foreground">
          Szerkesztve <span className="data-mono">{minute(row.edited_at)}</span>
        </p>
      </div>
    </article>
  );
}
